import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useDispatch, useSelector} from 'react-redux';
import {FileDropZone} from '~/common/components/FileDropZone/FileDropZone';
import {pluralize} from '~/common/utils';
import {useDeletePhoto} from '~/ViewInvitation/hooks/useDeletePhoto';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {
  CLEAR_FEATURED_PHOTO,
  CLEAR_PENDING_PHOTO,
  CLEAR_PENDING_PHOTOS,
  FEATURE_PHOTO,
  NAME_HOST_GALLERY,
} from '~/ViewInvitation/reducers/constants';
import {PhotoUploadButton} from '~/ViewInvitation/routes/Landing/PhotosModule/PhotoUploadButton';
import {
  PhotoContextMenu,
  PhotoDeleteAllConfirmationModal,
  PhotoDeleteConfirmationModal,
  PhotoFeatureConfirmationModal,
} from '~/ViewInvitation/routes/Photos/modals';
import {PhotoGalleryTile} from '~/ViewInvitation/routes/Photos/PhotoGalleryTile';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {PhotosEmptyState} from '~/ViewInvitation/routes/Photos/PhotosEmptyState/PhotosEmptyState';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import {selectFailedPhotosCount, selectHostGalleryName} from '~/ViewInvitation/selectors/photos';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {AdaptiveModal} from '~library/organisms/AdaptiveModal';
import {ChevronsDownIcon} from '~library/svg/ChevronsDownIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {charcoal, coin} from '~libSass/base/_exports.sass';

import './PhotoGalleryEditor.sass';

const ABOVE_FOLD_PHOTO_COUNT = 16;

const loadingSkeletons = (count) =>
  new Array(count).fill(null).map((_, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: allow index as they're all identical
    <Skeleton key={i} className="photo-gallery__photo-tile-skeleton" />
  ));

// TODO: refactor into smaller sub-components
export const PhotoGalleryEditor = ({
  isOpen,
  onClose,
  stagedPhotos = [],
  isHostModule = false,
  collection = 'photos',
  handlePost,
  doFeature,
  onViewPhotoFn,
  isPosting,
  setPhotoGalleryOpen,
  onEditGallery,
}) => {
  const dispatch = useDispatch();
  const listRef = useRef(null);
  const fileUploadRef = useRef(null);
  const isMediumAndUp = useIsMediumAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const failedPhotoCount = useSelector(selectFailedPhotosCount);
  const eventId = useSelector(selectEventId);
  const galleryName = useSelector(selectHostGalleryName);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showingFeatureMessage, setShowingFeatureMessage] = useState(false);
  const [showingContextMenu, setShowingContextMenu] = useState(false);
  const [showingDeleteMessage, setShowingDeleteMessage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showingDeleteAllMessage, setShowingDeleteAllMessage] = useState(false);
  const [photoToFeature, setPhotoToFeature] = useState({});
  const [newIndicatorHidden, setNewIndicatorHidden] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feedback =
    failedPhotoCount > 0 ? `${pluralize('photo', failedPhotoCount)} failed to upload` : null;

  const {photos, isLoading} = usePhotos({
    eventId,
    isHostModule,
    perPage: -1,
    includeCount: true,
  });

  const combinedPhotos = useMemo(() => {
    if (!isHostModule) return [...stagedPhotos];
    const newArr = [...photos, ...stagedPhotos];

    const idx = newArr.findIndex((item) => item.position === 1);
    if (idx > 0) {
      const [obj] = newArr.splice(idx, 1);
      newArr.unshift(obj);
    }
    return newArr;
  }, [photos, stagedPhotos, isHostModule]);

  const photoCount = combinedPhotos?.length;

  const {deleteCurrentPhoto, deleteAllPhotos} = useDeletePhoto({
    photos,
    photoCount,
    setShowingDeleteMessage,
    setShowingContextMenu,
    isHostModule,
  });

  const onGalleryNameChange = useCallback(
    (value) => {
      dispatch({type: NAME_HOST_GALLERY, payload: value});
    },
    [dispatch]
  );

  const handleFeaturePhoto = useCallback(
    (photo, isStaged, src = null, isFeatured = false) => {
      setPhotoToFeature({
        ...photo,
        isStaged,
        src,
        position: isHostModule ? 1 : null,
        isFeatured: isHostModule ? isFeatured : null,
      });
      setPhotoToDelete(photo.id);
      if (isLargishAndUp) {
        setShowingFeatureMessage(true);
      } else {
        setShowingContextMenu(true);
      }
    },
    [isLargishAndUp, isHostModule]
  );

  const confirmFeaturePhoto = useCallback(async () => {
    try {
      const {isStaged} = photoToFeature;
      await doFeature(photoToFeature, isStaged);
      dispatch({type: CLEAR_FEATURED_PHOTO});
      if (isStaged) {
        dispatch({
          type: FEATURE_PHOTO,
          payload: photoToFeature,
        });
      }
      setShowingFeatureMessage(false);
      setShowingContextMenu(false);
    } catch (e) {
      toastAlert(`Error uploading photos: ${e.message}`, {type: 'error'});
    }
  }, [dispatch, photoToFeature, doFeature]);

  const handleDeletePhoto = useCallback((id) => {
    setPhotoToDelete(id);
    setShowingDeleteMessage(true);
  }, []);

  const handleDeleteAllPhotos = useCallback(() => {
    setShowingDeleteAllMessage(true);
  }, []);

  const handleSave = useCallback(() => {
    setSubmitted(true);
    handlePost();
  }, [handlePost]);

  const deletePhoto = useCallback(async () => {
    setDeleting(true);
    // If photo is saved, DELETE
    if (photos.some((obj) => obj.id === photoToDelete)) {
      await deleteCurrentPhoto(null, photoToDelete);
      // Else clear from store
    } else {
      dispatch({
        type: CLEAR_PENDING_PHOTO,
        payload: {
          id: photoToDelete,
          collection,
        },
      });
    }
    setShowingDeleteMessage(false);
    setShowingContextMenu(false);
    setDeleting(false);
    setPhotoToDelete(null);
  }, [dispatch, photos, collection, photoToDelete, deleteCurrentPhoto]);

  const doDeleteAllPhotos = useCallback(async () => {
    if (isHostModule) {
      await deleteAllPhotos();
    }
    dispatch({type: CLEAR_PENDING_PHOTOS});
    setShowingDeleteAllMessage(false);
  }, [dispatch, deleteAllPhotos, isHostModule]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    if (stagedPhotos.length > 0) {
      setNewIndicatorHidden(false);
    }
  }, [stagedPhotos]);

  const spinner = isPosting ? <span className="evt-spinner inline no-margin" /> : null;
  const primaryActionText = (
    <>
      {spinner} {isHostModule ? 'Save' : 'Post photos'}
    </>
  );

  const showNewIndicator =
    combinedPhotos?.length > ABOVE_FOLD_PHOTO_COUNT && stagedPhotos?.length > 0 && isHostModule;

  return (
    <>
      <AdaptiveModal
        primaryQaId="photo-editor-modal"
        id="photo-editor-modal"
        headerText={isHostModule ? 'Edit gallery' : 'Post photos'}
        headerTextVariant="header5"
        primaryFooterActionText={primaryActionText}
        primaryFooterAction={handleSave}
        secondaryFooterAction={onClose}
        onClose={onClose}
        secondaryFooterActionText="Cancel"
        isOpen={isOpen}
        feedback={feedback}
      >
        <div
          className={cx('photo-editor__wrapper', {
            'photo-editor__wrapper--disabled': submitted,
          })}
        >
          {isHostModule && (
            <Input
              className="gallery-name-input"
              label="Gallery Name"
              data-qa-id="gallery-name"
              type="text"
              value={galleryName}
              onChange={(e) => onGalleryNameChange(e.target.value)}
              placeholder="e.g. Our Engagement Photos"
              maxLength={60}
            />
          )}
          <div className="photo-editor__content">
            <div className="photo-editor__content__header">
              <Typography variant="paragraph1" semibold>
                Photos ({photoCount})
              </Typography>
              <div className="photo-editor__actions">
                {!!combinedPhotos.length && (
                  <Button
                    variant="secondary"
                    size="small"
                    tight={!isLargishAndUp}
                    data-qa-id="delete-all-photos"
                    onClick={handleDeleteAllPhotos}
                    disabled={isLoading}
                    icon={
                      !isLargishAndUp
                        ? <TrashIcon color={isLoading ? coin : charcoal} ratio={0.5} />
                        : null
                    }
                  >
                    <Typography variant="paragraph2">
                      {isLargishAndUp ? 'Delete all' : 'All'}
                    </Typography>
                  </Button>
                )}
                <PhotoUploadButton
                  variant="secondary"
                  fileUploadRef={fileUploadRef}
                  disabled={isLoading}
                  iconColor={charcoal}
                >
                  <Typography variant="paragraph2">Add</Typography>
                </PhotoUploadButton>
              </div>
            </div>
            {photoCount === 0 && !isLoading
              ? <FileDropZone inputRef={fileUploadRef} hasBorder>
                  <PhotosEmptyState
                    emptyStateText="Psst... add photos to share with other guests!"
                    collection={collection}
                    fileUploadRef={fileUploadRef}
                  />
                </FileDropZone>
              : <div
                  className={cx('photo-editor__photos', {
                    'photo-editor__photos-host': isHostModule,
                  })}
                  ref={listRef}
                >
                  {isLoading
                    ? loadingSkeletons(isMediumAndUp ? 20 : 15)
                    : combinedPhotos?.map((p, i) => (
                        <PhotoGalleryTile
                          key={p.id}
                          photo={p}
                          isHostModule={isHostModule}
                          featured={i === 0}
                          onViewPhotoFn={onViewPhotoFn}
                          handleFeaturePhoto={handleFeaturePhoto}
                          handleDeletePhoto={handleDeletePhoto}
                          handleOpenContextMenu={handleFeaturePhoto}
                        />
                      ))}
                </div>}
            {showNewIndicator && (
              <Button
                className={cx('photo-gallery__new-indicator', {
                  'photo-gallery__new-indicator--hidden': newIndicatorHidden,
                })}
                variant="unstyled-button"
                data-qa-id="photo-gallery-new-indicator"
                aria-label="New photos added"
                onClick={scrollToBottom}
                icon={<ChevronsDownIcon />}
                blurry
              >
                <Typography variant="paragraph3">New</Typography>
              </Button>
            )}
          </div>
          <PhotoGalleryUploadInput
            fileUploadRef={fileUploadRef}
            collection={collection}
            setPhotoGalleryOpen={setPhotoGalleryOpen}
            onEditGallery={onEditGallery}
          />
        </div>
      </AdaptiveModal>
      {showingFeatureMessage && (
        <PhotoFeatureConfirmationModal
          onApprove={confirmFeaturePhoto}
          onClose={() => {
            if (photoToFeature.isStaged) {
              dispatch({type: CLEAR_FEATURED_PHOTO});
            }
            setShowingFeatureMessage(false);
          }}
        />
      )}
      {!isLargishAndUp && showingContextMenu && (
        <PhotoContextMenu
          photo={photoToFeature}
          onFeaturePhoto={confirmFeaturePhoto}
          onDeletePhoto={deletePhoto}
          isHostModule={isHostModule}
          deleting={deleting}
          onClose={() => {
            setShowingContextMenu(false);
            setPhotoToDelete(null);
          }}
        />
      )}
      {showingDeleteMessage && (
        <PhotoDeleteConfirmationModal
          onApprove={deletePhoto}
          onClose={() => {
            setPhotoToDelete(null);
            setShowingDeleteMessage(false);
          }}
          isHostModule={isHostModule}
        />
      )}
      {showingDeleteAllMessage && (
        <PhotoDeleteAllConfirmationModal
          onApprove={doDeleteAllPhotos}
          onClose={() => setShowingDeleteAllMessage(false)}
          photoCount={photoCount}
          isHostModule={isHostModule}
        />
      )}
    </>
  );
};
